/**
 * @fileoverview Admin Games Settings Screen
 * @description Configure game-specific settings like Lottie animations (Super Admin only)
 */

import React, { useState, useCallback, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  RefreshControl,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import LottieView from "lottie-react-native";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import { LottiePicker } from "@/components/LottiePicker";
import { getLottieSource, getLottieByKey } from "@/lib/lottie-registry";

interface GameConfig {
  id: string;
  slug: string;
  name: string;
  icon: string;
  color: string;
  default_config: Record<string, unknown>;
}

interface SudokuLottieConfig {
  win_lottie: string;
  lose_lottie: string;
  perfect_lottie: string;
}

interface RunnerLottieConfig {
  high_score_lottie: string;
  game_over_lottie: string;
}

interface RunnerAbilitySettings {
  opa: {
    jumpForceMultiplier: number;
    maxSpecialJumps: number;
  };
  oma: {
    lives: number;
    invincibilityDuration: number;
  };
}

interface Match3LottieConfig {
  win_lottie: string;
  high_score_lottie: string;
}

interface Match3TimeBonusConfig {
  score_threshold: number;
  bonus_seconds: number;
}

interface Match3GameSettings {
  initial_time_seconds: number;
  time_bonuses: Match3TimeBonusConfig[];
}

const DEFAULT_SUDOKU_LOTTIES: SudokuLottieConfig = {
  win_lottie: "Partying_Emoji",
  lose_lottie: "Loudly_Crying_Emoji",
  perfect_lottie: "Success",
};

const DEFAULT_RUNNER_LOTTIES: RunnerLottieConfig = {
  high_score_lottie: "trophy",
  game_over_lottie: "Partying_Emoji",
};

const DEFAULT_RUNNER_ABILITIES: RunnerAbilitySettings = {
  opa: {
    jumpForceMultiplier: 1.25,
    maxSpecialJumps: 3,
  },
  oma: {
    lives: 3,
    invincibilityDuration: 1500,
  },
};

const DEFAULT_MATCH3_LOTTIES: Match3LottieConfig = {
  win_lottie: "Partying_Emoji",
  high_score_lottie: "trophy",
};

const DEFAULT_MATCH3_SETTINGS: Match3GameSettings = {
  initial_time_seconds: 120,
  time_bonuses: [
    { score_threshold: 500, bonus_seconds: 15 },
    { score_threshold: 1500, bonus_seconds: 20 },
    { score_threshold: 3000, bonus_seconds: 30 },
    { score_threshold: 5000, bonus_seconds: 45 },
  ],
};

/**
 * Admin Games Settings Screen
 */
export default function GamesSettingsScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { roles } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [games, setGames] = useState<GameConfig[]>([]);
  const [selectedGame, setSelectedGame] = useState<string | null>(null);

  // Sudoku lottie config
  const [sudokuLotties, setSudokuLotties] = useState<SudokuLottieConfig>(
    DEFAULT_SUDOKU_LOTTIES
  );

  // Endless Runner lottie config
  const [runnerLotties, setRunnerLotties] = useState<RunnerLottieConfig>(
    DEFAULT_RUNNER_LOTTIES
  );

  // Endless Runner ability settings
  const [runnerAbilities, setRunnerAbilities] = useState<RunnerAbilitySettings>(
    DEFAULT_RUNNER_ABILITIES
  );

  // Match3 config
  const [match3Lotties, setMatch3Lotties] = useState<Match3LottieConfig>(
    DEFAULT_MATCH3_LOTTIES
  );
  const [match3Settings, setMatch3Settings] = useState<Match3GameSettings>(
    DEFAULT_MATCH3_SETTINGS
  );

  // Only super admins can access this screen
  if (!roles.isSuperAdmin) {
    return (
      <SafeAreaView
        className="flex-1"
        style={{ backgroundColor: colors.background }}
        edges={["top", "bottom"]}
      >
        <View className="flex-1 items-center justify-center px-6">
          <Ionicons name="lock-closed" size={64} color={colors.error} />
          <Text
            className="text-lg font-medium mt-4 text-center"
            style={{ color: colors.text }}
          >
            Geen toegang
          </Text>
          <Text
            className="mt-2 text-center"
            style={{ color: colors.textSecondary }}
          >
            Alleen super admins kunnen deze instellingen wijzigen.
          </Text>
          <TouchableOpacity
            onPress={() => router.back()}
            className="mt-6 px-6 py-3 rounded-xl"
            style={{ backgroundColor: colors.primary }}
          >
            <Text className="text-white font-semibold">Terug</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  /**
   * Fetch game definitions
   */
  const fetchGames = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("game_definitions")
        .select("id, slug, name, icon, color, default_config")
        .eq("is_active", true)
        .order("sort_order");

      if (error) throw error;

      setGames(data || []);

      // Load Sudoku config
      const sudokuGame = data?.find((g) => g.slug === "sudoku");
      if (sudokuGame?.default_config) {
        const config = sudokuGame.default_config as Record<string, unknown>;
        setSudokuLotties({
          win_lottie:
            (config.win_lottie as string) || DEFAULT_SUDOKU_LOTTIES.win_lottie,
          lose_lottie:
            (config.lose_lottie as string) || DEFAULT_SUDOKU_LOTTIES.lose_lottie,
          perfect_lottie:
            (config.perfect_lottie as string) ||
            DEFAULT_SUDOKU_LOTTIES.perfect_lottie,
        });
      }

      // Load Endless Runner config
      const runnerGame = data?.find((g) => g.slug === "endless_runner");
      if (runnerGame?.default_config) {
        const config = runnerGame.default_config as Record<string, unknown>;
        setRunnerLotties({
          high_score_lottie:
            (config.high_score_lottie as string) ||
            DEFAULT_RUNNER_LOTTIES.high_score_lottie,
          game_over_lottie:
            (config.game_over_lottie as string) ||
            DEFAULT_RUNNER_LOTTIES.game_over_lottie,
        });
        // Load ability settings
        const abilityConfig = config.ability_settings as RunnerAbilitySettings | undefined;
        if (abilityConfig) {
          setRunnerAbilities({
            opa: {
              jumpForceMultiplier: abilityConfig.opa?.jumpForceMultiplier ?? DEFAULT_RUNNER_ABILITIES.opa.jumpForceMultiplier,
              maxSpecialJumps: abilityConfig.opa?.maxSpecialJumps ?? DEFAULT_RUNNER_ABILITIES.opa.maxSpecialJumps,
            },
            oma: {
              lives: abilityConfig.oma?.lives ?? DEFAULT_RUNNER_ABILITIES.oma.lives,
              invincibilityDuration: abilityConfig.oma?.invincibilityDuration ?? DEFAULT_RUNNER_ABILITIES.oma.invincibilityDuration,
            },
          });
        }
      }

      // Load Match3 config
      const match3Game = data?.find((g) => g.slug === "match_3");
      if (match3Game?.default_config) {
        const config = match3Game.default_config as Record<string, unknown>;
        setMatch3Lotties({
          win_lottie: (config.win_lottie as string) || DEFAULT_MATCH3_LOTTIES.win_lottie,
          high_score_lottie: (config.high_score_lottie as string) || DEFAULT_MATCH3_LOTTIES.high_score_lottie,
        });
        setMatch3Settings({
          initial_time_seconds: (config.initial_time_seconds as number) || DEFAULT_MATCH3_SETTINGS.initial_time_seconds,
          time_bonuses: (config.time_bonuses as Match3TimeBonusConfig[]) || DEFAULT_MATCH3_SETTINGS.time_bonuses,
        });
      }
    } catch (err) {
      console.error("[GamesSettings] Error fetching games:", err);
      Alert.alert("Fout", "Kon spellen niet laden");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  /**
   * Handle refresh
   */
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchGames();
    setIsRefreshing(false);
  };

  /**
   * Save Sudoku lottie config
   */
  const handleSaveSudokuLotties = async () => {
    setIsSaving(true);

    try {
      const sudokuGame = games.find((g) => g.slug === "sudoku");
      if (!sudokuGame) throw new Error("Sudoku niet gevonden");

      const updatedConfig = {
        ...(sudokuGame.default_config as Record<string, unknown>),
        win_lottie: sudokuLotties.win_lottie,
        lose_lottie: sudokuLotties.lose_lottie,
        perfect_lottie: sudokuLotties.perfect_lottie,
      };

      const { error } = await supabase
        .from("game_definitions")
        .update({
          default_config: updatedConfig,
          updated_at: new Date().toISOString(),
        })
        .eq("id", sudokuGame.id);

      if (error) throw error;

      Alert.alert("Opgeslagen", "Sudoku animaties zijn bijgewerkt");
      await fetchGames();
    } catch (err) {
      console.error("[GamesSettings] Error saving:", err);
      Alert.alert("Fout", "Kon instellingen niet opslaan");
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Save Endless Runner settings (lotties + abilities)
   */
  const handleSaveRunnerSettings = async () => {
    setIsSaving(true);

    try {
      const runnerGame = games.find((g) => g.slug === "endless_runner");
      if (!runnerGame) throw new Error("Endless Runner niet gevonden");

      const updatedConfig = {
        ...(runnerGame.default_config as Record<string, unknown>),
        high_score_lottie: runnerLotties.high_score_lottie,
        game_over_lottie: runnerLotties.game_over_lottie,
        ability_settings: runnerAbilities,
      };

      const { error } = await supabase
        .from("game_definitions")
        .update({
          default_config: updatedConfig,
          updated_at: new Date().toISOString(),
        })
        .eq("id", runnerGame.id);

      if (error) throw error;

      Alert.alert("Opgeslagen", "Endless Runner instellingen zijn bijgewerkt");
      await fetchGames();
    } catch (err) {
      console.error("[GamesSettings] Error saving:", err);
      Alert.alert("Fout", "Kon instellingen niet opslaan");
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Save Match3 settings
   */
  const handleSaveMatch3Settings = async () => {
    setIsSaving(true);

    try {
      const match3Game = games.find((g) => g.slug === "match_3");
      if (!match3Game) throw new Error("Match 3 niet gevonden");

      const updatedConfig = {
        ...(match3Game.default_config as Record<string, unknown>),
        win_lottie: match3Lotties.win_lottie,
        high_score_lottie: match3Lotties.high_score_lottie,
        initial_time_seconds: match3Settings.initial_time_seconds,
        time_bonuses: match3Settings.time_bonuses,
      };

      const { error } = await supabase
        .from("game_definitions")
        .update({
          default_config: updatedConfig,
          updated_at: new Date().toISOString(),
        })
        .eq("id", match3Game.id);

      if (error) throw error;

      Alert.alert("Opgeslagen", "Match 3 instellingen zijn bijgewerkt");
      await fetchGames();
    } catch (err) {
      console.error("[GamesSettings] Error saving:", err);
      Alert.alert("Fout", "Kon instellingen niet opslaan");
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Update a time bonus
   */
  const updateTimeBonus = (index: number, field: keyof Match3TimeBonusConfig, value: number) => {
    setMatch3Settings((prev) => {
      const newBonuses = [...prev.time_bonuses];
      newBonuses[index] = { ...newBonuses[index], [field]: value };
      return { ...prev, time_bonuses: newBonuses };
    });
  };

  /**
   * Add a new time bonus
   */
  const addTimeBonus = () => {
    setMatch3Settings((prev) => {
      // Find the highest threshold and add 1000 to it
      const maxThreshold = prev.time_bonuses.length > 0
        ? Math.max(...prev.time_bonuses.map(b => b.score_threshold))
        : 0;
      const newBonus: Match3TimeBonusConfig = {
        score_threshold: maxThreshold + 1000,
        bonus_seconds: 15,
      };
      return {
        ...prev,
        time_bonuses: [...prev.time_bonuses, newBonus],
      };
    });
  };

  /**
   * Remove a time bonus
   */
  const removeTimeBonus = (index: number) => {
    if (match3Settings.time_bonuses.length <= 1) {
      Alert.alert("Fout", "Je moet minimaal 1 tijd bonus hebben");
      return;
    }
    setMatch3Settings((prev) => ({
      ...prev,
      time_bonuses: prev.time_bonuses.filter((_, i) => i !== index),
    }));
  };

  /**
   * Render a Lottie preview
   */
  const renderLottiePreview = (key: string, label: string) => {
    const lottieItem = getLottieByKey(key);
    const source = getLottieSource(key);

    return (
      <View className="items-center">
        <View
          className="w-16 h-16 rounded-xl items-center justify-center"
          style={{ backgroundColor: colors.surfaceSecondary }}
        >
          {source ? (
            <LottieView
              source={source}
              style={{ width: 48, height: 48 }}
              autoPlay
              loop
            />
          ) : (
            <Ionicons name="help-circle" size={32} color={colors.textTertiary} />
          )}
        </View>
        <Text
          className="text-xs mt-1 text-center"
          style={{ color: colors.textSecondary }}
          numberOfLines={1}
        >
          {lottieItem?.name || key}
        </Text>
        <Text
          className="text-xs font-medium"
          style={{ color: colors.primary }}
        >
          {label}
        </Text>
      </View>
    );
  };

  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1"
        style={{ backgroundColor: colors.background }}
        edges={["top", "bottom"]}
      >
        <View className="flex-1 items-center justify-center">
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["top", "bottom"]}
    >
      {/* Header */}
      <View
        className="flex-row items-center px-4 py-3 border-b"
        style={{ borderColor: colors.border }}
      >
        <TouchableOpacity
          onPress={() => router.back()}
          className="w-10 h-10 items-center justify-center"
          accessibilityLabel="Terug"
        >
          <Ionicons name="arrow-back" size={24} color="#D97706" />
        </TouchableOpacity>
        <Text className="flex-1 text-xl font-bold ml-2" style={{ color: colors.text }}>
          Spellen Instellingen
        </Text>
        <View
          className="px-2 py-1 rounded-full"
          style={{ backgroundColor: "#DC262615" }}
        >
          <Text className="text-xs font-medium" style={{ color: "#DC2626" }}>
            Super Admin
          </Text>
        </View>
      </View>

      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
          />
        }
        contentContainerClassName="p-4"
      >
        {/* Info Banner */}
        <View
          className="rounded-xl p-4 mb-6 flex-row items-start"
          style={{ backgroundColor: `${colors.primary}15` }}
        >
          <Ionicons name="information-circle" size={24} color={colors.primary} />
          <View className="flex-1 ml-3">
            <Text className="font-semibold" style={{ color: colors.text }}>
              Lottie Animaties
            </Text>
            <Text
              className="text-sm mt-1"
              style={{ color: colors.textSecondary }}
            >
              Configureer de animaties die worden getoond bij het winnen of
              verliezen van spellen.
            </Text>
          </View>
        </View>

        {/* Sudoku Settings */}
        <View
          className="rounded-2xl p-4 mb-4"
          style={{
            backgroundColor: colors.surface,
            borderWidth: 1,
            borderColor: colors.border,
          }}
        >
          <TouchableOpacity
            onPress={() =>
              setSelectedGame(selectedGame === "sudoku" ? null : "sudoku")
            }
            className="flex-row items-center"
          >
            <View
              className="w-12 h-12 rounded-xl items-center justify-center mr-3"
              style={{ backgroundColor: "#8B5CF620" }}
            >
              <Ionicons name="grid-outline" size={24} color="#8B5CF6" />
            </View>
            <View className="flex-1">
              <Text className="font-semibold text-lg" style={{ color: colors.text }}>
                Sudoku
              </Text>
              <Text className="text-sm" style={{ color: colors.textSecondary }}>
                Configureer win/verlies animaties
              </Text>
            </View>
            <Ionicons
              name={selectedGame === "sudoku" ? "chevron-up" : "chevron-down"}
              size={24}
              color={colors.textSecondary}
            />
          </TouchableOpacity>

          {selectedGame === "sudoku" && (
            <View className="mt-4 pt-4 border-t" style={{ borderColor: colors.border }}>
              {/* Current Lotties Preview */}
              <Text
                className="text-sm font-medium mb-3"
                style={{ color: colors.textSecondary }}
              >
                Huidige animaties:
              </Text>
              <View className="flex-row justify-around mb-6">
                {renderLottiePreview(sudokuLotties.win_lottie, "Win")}
                {renderLottiePreview(sudokuLotties.perfect_lottie, "Perfect")}
                {renderLottiePreview(sudokuLotties.lose_lottie, "Verlies")}
              </View>

              {/* Win Lottie Picker */}
              <View className="mb-4">
                <LottiePicker
                  value={sudokuLotties.win_lottie}
                  onChange={(key) =>
                    setSudokuLotties((prev) => ({ ...prev, win_lottie: key }))
                  }
                  label="Win Animatie"
                  placeholder="Kies win animatie"
                />
              </View>

              {/* Perfect Lottie Picker */}
              <View className="mb-4">
                <LottiePicker
                  value={sudokuLotties.perfect_lottie}
                  onChange={(key) =>
                    setSudokuLotties((prev) => ({ ...prev, perfect_lottie: key }))
                  }
                  label="Perfecte Score Animatie"
                  placeholder="Kies perfecte score animatie"
                />
              </View>

              {/* Lose Lottie Picker */}
              <View className="mb-4">
                <LottiePicker
                  value={sudokuLotties.lose_lottie}
                  onChange={(key) =>
                    setSudokuLotties((prev) => ({ ...prev, lose_lottie: key }))
                  }
                  label="Verlies Animatie"
                  placeholder="Kies verlies animatie"
                />
              </View>

              {/* Save Button */}
              <TouchableOpacity
                onPress={handleSaveSudokuLotties}
                disabled={isSaving}
                className="py-3 rounded-xl items-center mt-2"
                style={{
                  backgroundColor: colors.primary,
                  opacity: isSaving ? 0.7 : 1,
                }}
              >
                {isSaving ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <Text className="font-semibold text-white">
                    Opslaan
                  </Text>
                )}
              </TouchableOpacity>

              {/* Reset Button */}
              <TouchableOpacity
                onPress={() => setSudokuLotties(DEFAULT_SUDOKU_LOTTIES)}
                className="py-3 rounded-xl items-center mt-2"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Text className="font-medium" style={{ color: colors.textSecondary }}>
                  Herstel standaard
                </Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        {/* Endless Runner Settings */}
        <View
          className="rounded-2xl p-4 mb-4"
          style={{
            backgroundColor: colors.surface,
            borderWidth: 1,
            borderColor: colors.border,
          }}
        >
          <TouchableOpacity
            onPress={() =>
              setSelectedGame(selectedGame === "endless_runner" ? null : "endless_runner")
            }
            className="flex-row items-center"
          >
            <View
              className="w-12 h-12 rounded-xl items-center justify-center mr-3"
              style={{ backgroundColor: "#F5900520" }}
            >
              <Ionicons name="walk-outline" size={24} color="#F59005" />
            </View>
            <View className="flex-1">
              <Text className="font-semibold text-lg" style={{ color: colors.text }}>
                Endless Runner
              </Text>
              <Text className="text-sm" style={{ color: colors.textSecondary }}>
                Configureer game over animaties
              </Text>
            </View>
            <Ionicons
              name={selectedGame === "endless_runner" ? "chevron-up" : "chevron-down"}
              size={24}
              color={colors.textSecondary}
            />
          </TouchableOpacity>

          {selectedGame === "endless_runner" && (
            <View className="mt-4 pt-4 border-t" style={{ borderColor: colors.border }}>
              {/* Current Lotties Preview */}
              <Text
                className="text-sm font-medium mb-3"
                style={{ color: colors.textSecondary }}
              >
                Huidige animaties:
              </Text>
              <View className="flex-row justify-around mb-6">
                {renderLottiePreview(runnerLotties.high_score_lottie, "Nieuw Record")}
                {renderLottiePreview(runnerLotties.game_over_lottie, "Game Over")}
              </View>

              {/* High Score Lottie Picker */}
              <View className="mb-4">
                <LottiePicker
                  value={runnerLotties.high_score_lottie}
                  onChange={(key) =>
                    setRunnerLotties((prev) => ({ ...prev, high_score_lottie: key }))
                  }
                  label="Nieuw Record Animatie"
                  placeholder="Kies nieuw record animatie"
                />
              </View>

              {/* Game Over Lottie Picker */}
              <View className="mb-4">
                <LottiePicker
                  value={runnerLotties.game_over_lottie}
                  onChange={(key) =>
                    setRunnerLotties((prev) => ({ ...prev, game_over_lottie: key }))
                  }
                  label="Game Over Animatie"
                  placeholder="Kies game over animatie"
                />
              </View>

              {/* Character Abilities Section */}
              <View className="mt-4 pt-4 border-t" style={{ borderColor: colors.border }}>
                <Text
                  className="text-sm font-medium mb-4"
                  style={{ color: colors.textSecondary }}
                >
                  Karakter Vaardigheden:
                </Text>

                {/* Opa Settings */}
                <View
                  className="rounded-xl p-3 mb-3"
                  style={{ backgroundColor: colors.surfaceSecondary }}
                >
                  <Text className="font-semibold mb-2" style={{ color: colors.text }}>
                    👴 Opa - Hoge Springer
                  </Text>
                  <View className="flex-row items-center justify-between mb-2">
                    <Text className="text-sm" style={{ color: colors.textSecondary }}>
                      Sprong verhoging:
                    </Text>
                    <View className="flex-row items-center gap-2">
                      <TouchableOpacity
                        onPress={() =>
                          setRunnerAbilities((prev) => ({
                            ...prev,
                            opa: {
                              ...prev.opa,
                              jumpForceMultiplier: Math.max(1.1, prev.opa.jumpForceMultiplier - 0.05),
                            },
                          }))
                        }
                        className="w-8 h-8 rounded-lg items-center justify-center"
                        style={{ backgroundColor: colors.surface }}
                      >
                        <Ionicons name="remove" size={18} color={colors.text} />
                      </TouchableOpacity>
                      <Text className="font-semibold w-12 text-center" style={{ color: colors.text }}>
                        {Math.round((runnerAbilities.opa.jumpForceMultiplier - 1) * 100)}%
                      </Text>
                      <TouchableOpacity
                        onPress={() =>
                          setRunnerAbilities((prev) => ({
                            ...prev,
                            opa: {
                              ...prev.opa,
                              jumpForceMultiplier: Math.min(1.5, prev.opa.jumpForceMultiplier + 0.05),
                            },
                          }))
                        }
                        className="w-8 h-8 rounded-lg items-center justify-center"
                        style={{ backgroundColor: colors.surface }}
                      >
                        <Ionicons name="add" size={18} color={colors.text} />
                      </TouchableOpacity>
                    </View>
                  </View>
                  <View className="flex-row items-center justify-between">
                    <Text className="text-sm" style={{ color: colors.textSecondary }}>
                      Max speciale sprongen:
                    </Text>
                    <View className="flex-row items-center gap-2">
                      <TouchableOpacity
                        onPress={() =>
                          setRunnerAbilities((prev) => ({
                            ...prev,
                            opa: {
                              ...prev.opa,
                              maxSpecialJumps: Math.max(1, prev.opa.maxSpecialJumps - 1),
                            },
                          }))
                        }
                        className="w-8 h-8 rounded-lg items-center justify-center"
                        style={{ backgroundColor: colors.surface }}
                      >
                        <Ionicons name="remove" size={18} color={colors.text} />
                      </TouchableOpacity>
                      <Text className="font-semibold w-12 text-center" style={{ color: colors.text }}>
                        {runnerAbilities.opa.maxSpecialJumps}x
                      </Text>
                      <TouchableOpacity
                        onPress={() =>
                          setRunnerAbilities((prev) => ({
                            ...prev,
                            opa: {
                              ...prev.opa,
                              maxSpecialJumps: Math.min(10, prev.opa.maxSpecialJumps + 1),
                            },
                          }))
                        }
                        className="w-8 h-8 rounded-lg items-center justify-center"
                        style={{ backgroundColor: colors.surface }}
                      >
                        <Ionicons name="add" size={18} color={colors.text} />
                      </TouchableOpacity>
                    </View>
                  </View>
                </View>

                {/* Oma Settings */}
                <View
                  className="rounded-xl p-3"
                  style={{ backgroundColor: colors.surfaceSecondary }}
                >
                  <Text className="font-semibold mb-2" style={{ color: colors.text }}>
                    👵 Oma - Drie Levens
                  </Text>
                  <View className="flex-row items-center justify-between mb-2">
                    <Text className="text-sm" style={{ color: colors.textSecondary }}>
                      Aantal levens:
                    </Text>
                    <View className="flex-row items-center gap-2">
                      <TouchableOpacity
                        onPress={() =>
                          setRunnerAbilities((prev) => ({
                            ...prev,
                            oma: {
                              ...prev.oma,
                              lives: Math.max(1, prev.oma.lives - 1),
                            },
                          }))
                        }
                        className="w-8 h-8 rounded-lg items-center justify-center"
                        style={{ backgroundColor: colors.surface }}
                      >
                        <Ionicons name="remove" size={18} color={colors.text} />
                      </TouchableOpacity>
                      <Text className="font-semibold w-12 text-center" style={{ color: colors.text }}>
                        {runnerAbilities.oma.lives}x
                      </Text>
                      <TouchableOpacity
                        onPress={() =>
                          setRunnerAbilities((prev) => ({
                            ...prev,
                            oma: {
                              ...prev.oma,
                              lives: Math.min(5, prev.oma.lives + 1),
                            },
                          }))
                        }
                        className="w-8 h-8 rounded-lg items-center justify-center"
                        style={{ backgroundColor: colors.surface }}
                      >
                        <Ionicons name="add" size={18} color={colors.text} />
                      </TouchableOpacity>
                    </View>
                  </View>
                  <View className="flex-row items-center justify-between">
                    <Text className="text-sm" style={{ color: colors.textSecondary }}>
                      Onkwetsbaarheid (sec):
                    </Text>
                    <View className="flex-row items-center gap-2">
                      <TouchableOpacity
                        onPress={() =>
                          setRunnerAbilities((prev) => ({
                            ...prev,
                            oma: {
                              ...prev.oma,
                              invincibilityDuration: Math.max(500, prev.oma.invincibilityDuration - 250),
                            },
                          }))
                        }
                        className="w-8 h-8 rounded-lg items-center justify-center"
                        style={{ backgroundColor: colors.surface }}
                      >
                        <Ionicons name="remove" size={18} color={colors.text} />
                      </TouchableOpacity>
                      <Text className="font-semibold w-12 text-center" style={{ color: colors.text }}>
                        {(runnerAbilities.oma.invincibilityDuration / 1000).toFixed(1)}s
                      </Text>
                      <TouchableOpacity
                        onPress={() =>
                          setRunnerAbilities((prev) => ({
                            ...prev,
                            oma: {
                              ...prev.oma,
                              invincibilityDuration: Math.min(3000, prev.oma.invincibilityDuration + 250),
                            },
                          }))
                        }
                        className="w-8 h-8 rounded-lg items-center justify-center"
                        style={{ backgroundColor: colors.surface }}
                      >
                        <Ionicons name="add" size={18} color={colors.text} />
                      </TouchableOpacity>
                    </View>
                  </View>
                </View>
              </View>

              {/* Save Button */}
              <TouchableOpacity
                onPress={handleSaveRunnerSettings}
                disabled={isSaving}
                className="py-3 rounded-xl items-center mt-4"
                style={{
                  backgroundColor: colors.primary,
                  opacity: isSaving ? 0.7 : 1,
                }}
              >
                {isSaving ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <Text className="font-semibold text-white">
                    Opslaan
                  </Text>
                )}
              </TouchableOpacity>

              {/* Reset Button */}
              <TouchableOpacity
                onPress={() => {
                  setRunnerLotties(DEFAULT_RUNNER_LOTTIES);
                  setRunnerAbilities(DEFAULT_RUNNER_ABILITIES);
                }}
                className="py-3 rounded-xl items-center mt-2"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Text className="font-medium" style={{ color: colors.textSecondary }}>
                  Herstel standaard
                </Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        {/* Match3 Settings */}
        <View
          className="rounded-2xl p-4 mb-4"
          style={{
            backgroundColor: colors.surface,
            borderWidth: 1,
            borderColor: colors.border,
          }}
        >
          <TouchableOpacity
            onPress={() =>
              setSelectedGame(selectedGame === "match_3" ? null : "match_3")
            }
            className="flex-row items-center"
          >
            <View
              className="w-12 h-12 rounded-xl items-center justify-center mr-3"
              style={{ backgroundColor: "#EC489920" }}
            >
              <Ionicons name="diamond-outline" size={24} color="#EC4899" />
            </View>
            <View className="flex-1">
              <Text className="font-semibold text-lg" style={{ color: colors.text }}>
                Match 3
              </Text>
              <Text className="text-sm" style={{ color: colors.textSecondary }}>
                Tijd & bonus instellingen
              </Text>
            </View>
            <Ionicons
              name={selectedGame === "match_3" ? "chevron-up" : "chevron-down"}
              size={24}
              color={colors.textSecondary}
            />
          </TouchableOpacity>

          {selectedGame === "match_3" && (
            <View className="mt-4 pt-4 border-t" style={{ borderColor: colors.border }}>
              {/* Current Lotties Preview */}
              <Text
                className="text-sm font-medium mb-3"
                style={{ color: colors.textSecondary }}
              >
                Huidige animaties:
              </Text>
              <View className="flex-row justify-around mb-6">
                {renderLottiePreview(match3Lotties.win_lottie, "Win")}
                {renderLottiePreview(match3Lotties.high_score_lottie, "Nieuw Record")}
              </View>

              {/* Win Lottie Picker */}
              <View className="mb-4">
                <LottiePicker
                  value={match3Lotties.win_lottie}
                  onChange={(key) =>
                    setMatch3Lotties((prev) => ({ ...prev, win_lottie: key }))
                  }
                  label="Win Animatie"
                  placeholder="Kies win animatie"
                />
              </View>

              {/* High Score Lottie Picker */}
              <View className="mb-4">
                <LottiePicker
                  value={match3Lotties.high_score_lottie}
                  onChange={(key) =>
                    setMatch3Lotties((prev) => ({ ...prev, high_score_lottie: key }))
                  }
                  label="Nieuw Record Animatie"
                  placeholder="Kies nieuw record animatie"
                />
              </View>

              {/* Time Settings Section */}
              <View className="mt-4 pt-4 border-t" style={{ borderColor: colors.border }}>
                <Text
                  className="text-sm font-medium mb-4"
                  style={{ color: colors.textSecondary }}
                >
                  Tijd Instellingen:
                </Text>

                {/* Initial Time */}
                <View
                  className="rounded-xl p-3 mb-3"
                  style={{ backgroundColor: colors.surfaceSecondary }}
                >
                  <View className="flex-row items-center justify-between">
                    <Text className="font-medium" style={{ color: colors.text }}>
                      Start tijd (seconden)
                    </Text>
                    <View className="flex-row items-center gap-2">
                      <TouchableOpacity
                        onPress={() =>
                          setMatch3Settings((prev) => ({
                            ...prev,
                            initial_time_seconds: Math.max(30, prev.initial_time_seconds - 15),
                          }))
                        }
                        className="w-8 h-8 rounded-lg items-center justify-center"
                        style={{ backgroundColor: colors.surface }}
                      >
                        <Ionicons name="remove" size={18} color={colors.text} />
                      </TouchableOpacity>
                      <Text className="font-semibold w-12 text-center" style={{ color: colors.text }}>
                        {match3Settings.initial_time_seconds}s
                      </Text>
                      <TouchableOpacity
                        onPress={() =>
                          setMatch3Settings((prev) => ({
                            ...prev,
                            initial_time_seconds: Math.min(300, prev.initial_time_seconds + 15),
                          }))
                        }
                        className="w-8 h-8 rounded-lg items-center justify-center"
                        style={{ backgroundColor: colors.surface }}
                      >
                        <Ionicons name="add" size={18} color={colors.text} />
                      </TouchableOpacity>
                    </View>
                  </View>
                </View>

                {/* Time Bonuses */}
                <View className="flex-row items-center justify-between mb-2 mt-2">
                  <Text
                    className="text-sm font-medium"
                    style={{ color: colors.textSecondary }}
                  >
                    Tijd Bonussen (extra tijd bij score):
                  </Text>
                  <TouchableOpacity
                    onPress={addTimeBonus}
                    className="flex-row items-center gap-1 px-2 py-1 rounded-lg"
                    style={{ backgroundColor: colors.primary + "20" }}
                    accessibilityLabel="Bonus toevoegen"
                    accessibilityRole="button"
                  >
                    <Ionicons name="add-circle" size={16} color={colors.primary} />
                    <Text className="text-xs font-medium" style={{ color: colors.primary }}>
                      Toevoegen
                    </Text>
                  </TouchableOpacity>
                </View>
                {match3Settings.time_bonuses.map((bonus, index) => (
                  <View
                    key={index}
                    className="rounded-xl p-3 mb-2"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                  >
                    {/* Header with bonus number and delete button */}
                    <View className="flex-row items-center justify-between mb-2">
                      <Text className="text-xs font-medium" style={{ color: colors.textTertiary }}>
                        Bonus {index + 1}
                      </Text>
                      <TouchableOpacity
                        onPress={() => removeTimeBonus(index)}
                        className="w-6 h-6 rounded-full items-center justify-center"
                        style={{ backgroundColor: colors.error + "20" }}
                        accessibilityLabel={`Verwijder bonus ${index + 1}`}
                        accessibilityRole="button"
                      >
                        <Ionicons name="trash-outline" size={12} color={colors.error} />
                      </TouchableOpacity>
                    </View>
                    <View className="flex-row items-center justify-between mb-2">
                      <Text className="text-sm" style={{ color: colors.textSecondary }}>
                        Score drempel:
                      </Text>
                      <View className="flex-row items-center gap-1">
                        <TouchableOpacity
                          onPress={() => updateTimeBonus(index, "score_threshold", Math.max(100, bonus.score_threshold - 100))}
                          className="w-7 h-7 rounded-lg items-center justify-center"
                          style={{ backgroundColor: colors.surface }}
                        >
                          <Ionicons name="remove" size={14} color={colors.text} />
                        </TouchableOpacity>
                        <Text className="font-medium w-14 text-center text-sm" style={{ color: colors.text }}>
                          {bonus.score_threshold}
                        </Text>
                        <TouchableOpacity
                          onPress={() => updateTimeBonus(index, "score_threshold", bonus.score_threshold + 100)}
                          className="w-7 h-7 rounded-lg items-center justify-center"
                          style={{ backgroundColor: colors.surface }}
                        >
                          <Ionicons name="add" size={14} color={colors.text} />
                        </TouchableOpacity>
                      </View>
                    </View>
                    <View className="flex-row items-center justify-between">
                      <Text className="text-sm" style={{ color: colors.textSecondary }}>
                        Bonus seconden:
                      </Text>
                      <View className="flex-row items-center gap-1">
                        <TouchableOpacity
                          onPress={() => updateTimeBonus(index, "bonus_seconds", Math.max(5, bonus.bonus_seconds - 5))}
                          className="w-7 h-7 rounded-lg items-center justify-center"
                          style={{ backgroundColor: colors.surface }}
                        >
                          <Ionicons name="remove" size={14} color={colors.text} />
                        </TouchableOpacity>
                        <Text className="font-medium w-10 text-center text-sm" style={{ color: colors.success }}>
                          +{bonus.bonus_seconds}s
                        </Text>
                        <TouchableOpacity
                          onPress={() => updateTimeBonus(index, "bonus_seconds", bonus.bonus_seconds + 5)}
                          className="w-7 h-7 rounded-lg items-center justify-center"
                          style={{ backgroundColor: colors.surface }}
                        >
                          <Ionicons name="add" size={14} color={colors.text} />
                        </TouchableOpacity>
                      </View>
                    </View>
                  </View>
                ))}

                {/* Empty state if no bonuses */}
                {match3Settings.time_bonuses.length === 0 && (
                  <View
                    className="rounded-xl p-4 items-center"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                  >
                    <Text className="text-sm" style={{ color: colors.textSecondary }}>
                      Geen tijd bonussen geconfigureerd
                    </Text>
                    <TouchableOpacity
                      onPress={addTimeBonus}
                      className="mt-2 px-4 py-2 rounded-lg"
                      style={{ backgroundColor: colors.primary }}
                    >
                      <Text className="text-white font-medium text-sm">
                        Voeg eerste bonus toe
                      </Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>

              {/* Save Button */}
              <TouchableOpacity
                onPress={handleSaveMatch3Settings}
                disabled={isSaving}
                className="py-3 rounded-xl items-center mt-4"
                style={{
                  backgroundColor: colors.primary,
                  opacity: isSaving ? 0.7 : 1,
                }}
              >
                {isSaving ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <Text className="font-semibold text-white">
                    Opslaan
                  </Text>
                )}
              </TouchableOpacity>

              {/* Reset Button */}
              <TouchableOpacity
                onPress={() => {
                  setMatch3Lotties(DEFAULT_MATCH3_LOTTIES);
                  setMatch3Settings(DEFAULT_MATCH3_SETTINGS);
                }}
                className="py-3 rounded-xl items-center mt-2"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Text className="font-medium" style={{ color: colors.textSecondary }}>
                  Herstel standaard
                </Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        {/* Memory - navigates to dedicated image management screen */}
        {games.find((g) => g.slug === "puzzle_matching") && (
          <TouchableOpacity
            onPress={() => router.push("/(admin)/games/memory")}
            className="rounded-2xl p-4 mb-4 flex-row items-center"
            style={{
              backgroundColor: colors.surface,
              borderWidth: 1,
              borderColor: colors.border,
            }}
            accessibilityLabel="Memory afbeeldingen beheren"
            accessibilityRole="button"
          >
            <View
              className="w-12 h-12 rounded-xl items-center justify-center mr-3"
              style={{ backgroundColor: `${games.find((g) => g.slug === "puzzle_matching")!.color}20` }}
            >
              <Ionicons
                name={games.find((g) => g.slug === "puzzle_matching")!.icon as any}
                size={24}
                color={games.find((g) => g.slug === "puzzle_matching")!.color}
              />
            </View>
            <View className="flex-1">
              <Text className="font-semibold text-lg" style={{ color: colors.text }}>
                Memory
              </Text>
              <Text className="text-sm" style={{ color: colors.textSecondary }}>
                Afbeeldingen beheren
              </Text>
            </View>
            <Ionicons name="chevron-forward" size={24} color={colors.textSecondary} />
          </TouchableOpacity>
        )}

        {/* Other Games Placeholder */}
        {games
          .filter((g) => g.slug !== "sudoku" && g.slug !== "endless_runner" && g.slug !== "match_3" && g.slug !== "puzzle_matching")
          .map((game) => (
            <View
              key={game.slug}
              className="rounded-2xl p-4 mb-3 flex-row items-center opacity-50"
              style={{
                backgroundColor: colors.surface,
                borderWidth: 1,
                borderColor: colors.border,
              }}
            >
              <View
                className="w-12 h-12 rounded-xl items-center justify-center mr-3"
                style={{ backgroundColor: `${game.color}20` }}
              >
                <Ionicons name={game.icon as any} size={24} color={game.color} />
              </View>
              <View className="flex-1">
                <Text className="font-semibold text-lg" style={{ color: colors.text }}>
                  {game.name}
                </Text>
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  Binnenkort beschikbaar
                </Text>
              </View>
              <View
                className="px-2 py-1 rounded-full"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Text className="text-xs" style={{ color: colors.textTertiary }}>
                  Soon
                </Text>
              </View>
            </View>
          ))}

        <View className="h-8" />
      </ScrollView>
    </SafeAreaView>
  );
}

